import React, { useState, useEffect, useCallback } from "react";
import { Plus, Edit2, Trash2, Search, X, Loader, RefreshCw, Save, Send, Eye, FileText, Tag, AlertCircle } from "lucide-react";
import { blogAPI, blogCategoriesAPI, uploadAPI } from "../../services/api";
import { WysiwygEditor } from "../../components/ui/index";
import { getImageUrl } from "../../utils/image";

const toSlug = s => s.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/(^-|-$)/g,"");
const statusColors = { DRAFT:"bg-gray-100 text-gray-600", PUBLISHED:"bg-green-50 text-green-700", SCHEDULED:"bg-purple-50 text-purple-700" };

function CategoryManager({ categories, onRefresh }) {
  const [adding, setAdding]=useState(false);
  const [newName, setNewName]=useState("");
  const [editId, setEditId]=useState(null);
  const [editName, setEditName]=useState("");
  const [loading, setLoading]=useState(false);

  const handleAdd=async()=>{
    if(!newName.trim()) return;
    setLoading(true);
    try{await blogCategoriesAPI.create({name:newName.trim()}); setNewName(""); setAdding(false); onRefresh();}
    catch(err){alert(err.response?.data?.error||"Failed");}
    finally{setLoading(false);}
  };
  const handleUpdate=async(id)=>{
    if(!editName.trim()) return;
    setLoading(true);
    try{await blogCategoriesAPI.update(id,{name:editName.trim()}); setEditId(null); onRefresh();}
    catch{alert("Update failed");}
    finally{setLoading(false);}
  };
  const handleDelete=async(id,name)=>{
    if(!window.confirm(`Delete category "${name}"?`)) return;
    try{await blogCategoriesAPI.delete(id); onRefresh();}catch{alert("Delete failed");}
  };

  return (
    <div className="bg-white rounded-2xl shadow-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-gray-900">Blog Categories</h3>
        <button onClick={()=>setAdding(!adding)} className="btn-primary text-xs px-3 py-2"><Plus className="w-3.5 h-3.5"/>Add Category</button>
      </div>
      {adding && (
        <div className="bg-brand-pale rounded-xl p-4 mb-4 space-y-2">
          <input className="input-field text-sm" placeholder="Category name" value={newName} onChange={e=>setNewName(e.target.value)} autoFocus onKeyDown={e=>e.key==="Enter"&&handleAdd()} />
          <div className="flex gap-2">
            <button onClick={()=>{setAdding(false);setNewName("");}} className="flex-1 py-2 rounded-xl border border-gray-200 text-gray-600 text-xs">Cancel</button>
            <button onClick={handleAdd} disabled={loading||!newName.trim()} className="flex-1 btn-primary justify-center py-2 text-xs">{loading?<Loader className="w-3.5 h-3.5 animate-spin"/>:<><Save className="w-3.5 h-3.5"/>Save</>}</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {categories.length===0&&<p className="text-gray-400 text-sm text-center py-4">No categories yet.</p>}
        {categories.map(cat=>(
          <div key={cat.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl group">
            <Tag className="w-4 h-4 text-brand-primary flex-shrink-0"/>
            {editId===cat.id?(
              <div className="flex gap-2 flex-1">
                <input className="input-field text-sm py-1.5 flex-1" value={editName} onChange={e=>setEditName(e.target.value)} autoFocus onKeyDown={e=>e.key==="Enter"&&handleUpdate(cat.id)}/>
                <button onClick={()=>handleUpdate(cat.id)} className="px-3 py-1.5 rounded-lg bg-brand-primary text-white text-xs font-semibold">{loading?"...":"Save"}</button>
                <button onClick={()=>setEditId(null)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs">Cancel</button>
              </div>
            ):(
              <>
                <div className="flex-1 text-sm font-medium text-gray-800">{cat.name}</div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={()=>{setEditId(cat.id);setEditName(cat.name);}} className="w-7 h-7 rounded-lg bg-white flex items-center justify-center hover:bg-blue-50 hover:text-blue-600 text-gray-400 border border-gray-100"><Edit2 className="w-3 h-3"/></button>
                  <button onClick={()=>handleDelete(cat.id,cat.name)} className="w-7 h-7 rounded-lg bg-white flex items-center justify-center hover:bg-red-50 hover:text-red-500 text-gray-400 border border-gray-100"><Trash2 className="w-3 h-3"/></button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const emptyPost = {title:"",slug:"",excerpt:"",content:"",category:"",tags:"",imageUrl:"",status:"DRAFT",readTime:"5 min",featured:false,metaTitle:"",metaDesc:"",metaKeywords:""};

function BlogForm({post, categories, onSave, onClose}) {
  const [form, setForm]=useState(post?{...emptyPost,...post}:emptyPost);
  const [activeTab, setActiveTab]=useState("content");
  const [saving, setSaving]=useState(false);
  const [preview, setPreview]=useState(false);
  const [error, setError]=useState("");
  const [autoSlug, setAutoSlug]=useState(!post);
  const [bannerUploading, setBannerUploading]=useState(false);

  const set=k=>v=>setForm(f=>{const u={...f,[k]:v}; if(k==="title"&&autoSlug) u.slug=toSlug(v); return u;});

  const handleBannerUpload=async e=>{
    const file=e.target.files?.[0]; if(!file) return;
    setBannerUploading(true);
    try{const{data}=await uploadAPI.single(file,"blog"); set("imageUrl")(data.url);}
    catch{setError("Banner upload failed");}
    finally{setBannerUploading(false);}
  };

  const handleSave=async statusOverride=>{
    if(!form.title.trim()){setError("Title is required"); return;}
    if(!form.content||form.content==="<p></p>"){setError("Content is required"); return;}
    setSaving(true); setError("");
    try{
      const payload={...form};
      if(statusOverride) payload.status=statusOverride;
      if(!payload.slug) payload.slug=toSlug(payload.title);
      if(!payload.excerpt) payload.excerpt=payload.content.replace(/<[^>]+>/g,"").slice(0,250)+"...";
      await onSave(payload); onClose();
    }catch(err){setError(err.response?.data?.error||"Save failed");}
    finally{setSaving(false);}
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full my-6">
          <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-3xl z-10">
            <div className="flex items-center gap-3">
              <h3 className="font-heading font-bold text-gray-900 text-lg">{post?"Edit Post":"New Blog Post"}</h3>
              <span className={`badge text-xs px-2.5 py-1 rounded-full font-semibold ${statusColors[form.status]}`}>{form.status}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={()=>setPreview(!preview)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-100 text-gray-700 text-xs font-semibold hover:bg-gray-200"><Eye className="w-3.5 h-3.5"/>Preview</button>
              <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"><X className="w-4 h-4"/></button>
            </div>
          </div>
          {error&&<div className="mx-5 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0"/>{error}</div>}
          <div className="flex gap-1 px-5 pt-4 border-b border-gray-100">
            {["content","settings","seo"].map(t=>(
              <button key={t} onClick={()=>setActiveTab(t)} className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-all capitalize ${activeTab===t?"text-brand-primary border-b-2 border-brand-primary bg-brand-pale":"text-gray-500 hover:text-gray-700"}`}>{t}</button>
            ))}
          </div>
          <div className="p-5">
            {activeTab==="content"&&(
              <div className="space-y-5">
                <div><label className="block text-xs font-semibold text-gray-600 mb-1.5">Post Title *</label><input className="input-field text-base font-semibold" placeholder="Enter a compelling title..." value={form.title} onChange={e=>set("title")(e.target.value)}/></div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">URL Slug<button type="button" onClick={()=>setAutoSlug(!autoSlug)} className={`ml-2 text-xs px-2 py-0.5 rounded-full ${autoSlug?"bg-green-100 text-green-700":"bg-gray-100 text-gray-500"}`}>{autoSlug?"Auto":"Manual"}</button></label>
                  <div className="flex items-center gap-2"><span className="text-gray-400 text-sm">/blog/</span><input className="input-field text-sm flex-1" value={form.slug} onChange={e=>{setAutoSlug(false);set("slug")(toSlug(e.target.value));}}/></div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Featured Banner Image</label>
                  <div className="flex gap-2">
                    <input className="input-field text-sm flex-1" placeholder="Image URL..." value={form.imageUrl} onChange={e=>set("imageUrl")(e.target.value)}/>
                    <label className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold cursor-pointer flex-shrink-0 ${bannerUploading?"opacity-50":"hover:bg-brand-pale hover:border-brand-primary text-gray-600"}`}>
                      {bannerUploading?<Loader className="w-3.5 h-3.5 animate-spin"/>:"📷"} Upload
                      <input type="file" accept="image/*" className="hidden" onChange={handleBannerUpload} disabled={bannerUploading}/>
                    </label>
                  </div>
                  {form.imageUrl&&<img src={getImageUrl(form.imageUrl)} alt="" className="mt-2 h-32 w-full object-cover rounded-xl" onError={e=>e.target.style.display="none"}/>}
                </div>
                <div><label className="block text-xs font-semibold text-gray-600 mb-1.5">Excerpt / Summary</label><textarea className="input-field resize-none text-sm" rows={2} placeholder="Brief summary for listing page..." value={form.excerpt} onChange={e=>set("excerpt")(e.target.value)}/></div>
                <div><label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Content *</label><WysiwygEditor value={form.content} onChange={val=>set("content")(val)}/></div>
              </div>
            )}
            {activeTab==="settings"&&(
              <div className="space-y-5 max-w-xl">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label>
                    <select className="input-field text-sm" value={form.category} onChange={e=>set("category")(e.target.value)}>
                      <option value="">— Select —</option>
                      {categories.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                  <div><label className="block text-xs font-semibold text-gray-600 mb-1.5">Read Time</label><input className="input-field text-sm" value={form.readTime} onChange={e=>set("readTime")(e.target.value)} placeholder="e.g. 5 min"/></div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tags (comma separated)</label>
                  <input className="input-field text-sm" placeholder="food trade, export, quality..." value={form.tags} onChange={e=>set("tags")(e.target.value)}/>
                  {form.tags&&<div className="flex flex-wrap gap-1.5 mt-2">{form.tags.split(",").filter(Boolean).map(t=><span key={t} className="badge-brand text-xs">{t.trim()}</span>)}</div>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Status</label>
                  <div className="flex gap-3">{["DRAFT","PUBLISHED"].map(s=><button key={s} type="button" onClick={()=>set("status")(s)} className={`flex-1 py-2.5 rounded-xl text-xs font-bold border transition-all ${form.status===s?statusColors[s]+" border-current":"border-gray-200 text-gray-400 hover:border-gray-300"}`}>{s}</button>)}</div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.featured} onChange={e=>set("featured")(e.target.checked)} className="w-4 h-4 accent-brand-primary"/><span className="text-sm font-medium text-gray-700">Mark as Featured</span></label>
              </div>
            )}
            {activeTab==="seo"&&(
              <div className="space-y-4 max-w-xl">
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700">💡 Complete all SEO fields for better search ranking. Target 150+ words in content.</div>
                <div>
                  <div className="flex items-center justify-between mb-1.5"><label className="text-xs font-semibold text-gray-600">Meta Title</label><span className={`text-xs ${(form.metaTitle||"").length>60?"text-red-500":"text-gray-400"}`}>{(form.metaTitle||"").length}/60</span></div>
                  <input className="input-field text-sm" placeholder="SEO page title" value={form.metaTitle} onChange={e=>set("metaTitle")(e.target.value)}/>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5"><label className="text-xs font-semibold text-gray-600">Meta Description</label><span className={`text-xs ${(form.metaDesc||"").length>160?"text-red-500":"text-gray-400"}`}>{(form.metaDesc||"").length}/160</span></div>
                  <textarea className="input-field resize-none text-sm" rows={3} value={form.metaDesc} onChange={e=>set("metaDesc")(e.target.value)}/>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Meta Keywords</label>
                  <input className="input-field text-sm" placeholder="food trading, basmati rice, UAE export..." value={form.metaKeywords} onChange={e=>set("metaKeywords")(e.target.value)}/>
                  <p className="text-xs text-gray-400 mt-1">Separate with commas. Use 5–10 relevant keywords.</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between p-5 border-t border-gray-100 bg-gray-50 rounded-b-3xl">
            <button onClick={()=>handleSave("DRAFT")} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-white transition-colors">{saving?<Loader className="w-4 h-4 animate-spin"/>:<Save className="w-4 h-4"/>}Save Draft</button>
            <div className="flex gap-2">
              <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-white transition-colors">Cancel</button>
              <button onClick={()=>handleSave("PUBLISHED")} disabled={saving} className="btn-primary text-sm py-2.5">{saving?<Loader className="w-4 h-4 animate-spin"/>:<Send className="w-4 h-4"/>}Publish</button>
            </div>
          </div>
        </div>
      </div>
      {preview&&(
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full my-8">
            <div className="flex items-center justify-between p-5 border-b border-gray-100"><h3 className="font-heading font-bold text-gray-900">Preview</h3><button onClick={()=>setPreview(false)} className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500"><X className="w-4 h-4"/></button></div>
            <div className="p-8">
              {form.imageUrl&&<img src={getImageUrl(form.imageUrl)} alt="" className="w-full h-56 object-cover rounded-2xl mb-6" onError={e=>e.target.style.display="none"}/>}
              {form.category&&<span className="badge-brand mb-3 inline-block">{form.category}</span>}
              <h1 className="font-heading text-3xl font-bold text-gray-900 mb-3">{form.title||"Untitled"}</h1>
              {form.excerpt&&<p className="text-gray-500 text-base italic mb-6 border-l-4 border-brand-primary pl-4">{form.excerpt}</p>}
              <div className="rich-content" dangerouslySetInnerHTML={{__html:form.content||"<p>No content.</p>"}}/>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function AdminBlog() {
  const [posts, setPosts]=useState([]);
  const [categories, setCategories]=useState([]);
  const [loading, setLoading]=useState(true);
  const [search, setSearch]=useState("");
  const [filterStatus, setFilterStatus]=useState("ALL");
  const [showForm, setShowForm]=useState(false);
  const [editPost, setEditPost]=useState(null);
  const [activeTab, setActiveTab]=useState("posts");

  const fetchPosts=useCallback(async()=>{setLoading(true); try{const r=await blogAPI.getAllAdmin(); setPosts(r.data.posts||[]);}catch{setPosts([]);} finally{setLoading(false);}}, []);
  const fetchCategories=useCallback(async()=>{try{const r=await blogCategoriesAPI.getAll(); setCategories(r.data.categories||[]);}catch{}}, []);
  useEffect(()=>{fetchPosts(); fetchCategories();},[fetchPosts,fetchCategories]);

  const filtered=posts.filter(p=>{const matchStatus=filterStatus==="ALL"||p.status===filterStatus; const matchSearch=!search||p.title.toLowerCase().includes(search.toLowerCase()); return matchStatus&&matchSearch;});
  const handleSave=async data=>{
    const payload = { 
      ...data, 
      content: data.content ? "base64:" + btoa(unescape(encodeURIComponent(data.content))) : "" 
    };
    if(editPost) await blogAPI.update(editPost.id,payload); else await blogAPI.create(payload); fetchPosts();
  };
  const handleDelete=async id=>{if(!window.confirm("Delete this post?")) return; try{await blogAPI.delete(id); fetchPosts();}catch{}};

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h2 className="font-heading font-bold text-gray-900 text-xl">Blog</h2><p className="text-gray-400 text-sm">{posts.length} posts · {categories.length} categories</p></div>
        <div className="flex gap-2">
          <button onClick={()=>{fetchPosts();fetchCategories();}} className="w-9 h-9 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-soft"><RefreshCw className="w-4 h-4 text-gray-500"/></button>
          {activeTab==="posts"&&<button onClick={()=>{setEditPost(null);setShowForm(true);}} className="btn-primary text-xs px-4 py-2.5"><Plus className="w-4 h-4"/>New Post</button>}
        </div>
      </div>
      <div className="flex gap-2 border-b border-gray-100 pb-2">
        <button onClick={()=>setActiveTab("posts")} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${activeTab==="posts"?"bg-brand-pale text-brand-primary":"text-gray-500 hover:bg-gray-50"}`}><FileText className="w-4 h-4"/>Posts</button>
        <button onClick={()=>setActiveTab("categories")} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${activeTab==="categories"?"bg-brand-pale text-brand-primary":"text-gray-500 hover:bg-gray-50"}`}><Tag className="w-4 h-4"/>Categories</button>
      </div>
      {activeTab==="categories"&&<CategoryManager categories={categories} onRefresh={fetchCategories}/>}
      {activeTab==="posts"&&(
        <>
          <div className="flex flex-wrap gap-2 items-center">
            {["ALL","DRAFT","PUBLISHED"].map(s=><button key={s} onClick={()=>setFilterStatus(s)} className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${filterStatus===s?"bg-brand-primary text-white":"bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{s} ({s==="ALL"?posts.length:posts.filter(p=>p.status===s).length})</button>)}
            <div className="relative ml-auto"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/><input className="input-field pl-9 py-2 text-sm w-44" placeholder="Search posts..." value={search} onChange={e=>setSearch(e.target.value)}/></div>
          </div>
          <div className="bg-white rounded-2xl shadow-card overflow-hidden">
            {loading?<div className="flex items-center justify-center py-16"><Loader className="w-6 h-6 text-brand-primary animate-spin mr-2"/><span className="text-gray-400 text-sm">Loading...</span></div>:(
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="bg-gray-50 border-b border-gray-100">{["Title","Category","Meta Keywords","Status","Date","Actions"].map(h=><th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {filtered.length===0?<tr><td colSpan={6} className="px-5 py-12 text-center text-gray-400"><FileText className="w-10 h-10 text-gray-200 mx-auto mb-2"/>No posts found</td></tr>
                    :filtered.map(post=>(
                      <tr key={post.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-3.5"><div className="font-semibold text-gray-900 text-sm truncate max-w-xs">{post.title}</div><div className="text-xs text-gray-400">/blog/{post.slug}</div></td>
                        <td className="px-5 py-3.5">{post.category&&<span className="badge-brand text-xs">{post.category}</span>}</td>
                        <td className="px-5 py-3.5 text-xs text-gray-400 max-w-32 truncate">{post.metaKeywords||"—"}</td>
                        <td className="px-5 py-3.5"><span className={`badge text-xs px-2.5 py-1 rounded-full font-semibold ${statusColors[post.status]}`}>{post.status}</span></td>
                        <td className="px-5 py-3.5 text-xs text-gray-400">{post.publishedAt?new Date(post.publishedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):new Date(post.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1.5">
                            <button onClick={()=>{setEditPost(post);setShowForm(true);}} className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-blue-50 hover:text-blue-600 transition-colors text-gray-500"><Edit2 className="w-3.5 h-3.5"/></button>
                            <button onClick={()=>handleDelete(post.id)} className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors text-gray-500"><Trash2 className="w-3.5 h-3.5"/></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      {showForm&&<BlogForm post={editPost} categories={categories} onSave={handleSave} onClose={()=>{setShowForm(false);setEditPost(null);}}/>}
    </div>
  );
}
